import toast from 'react-hot-toast';
import { api } from '../hooks/useAuth';

export const DESKTOP_RECORDER_PROTOCOL = 'etr-video-recorder';

/** Override in .env.local: REACT_APP_RECORDER_EXE_PATH=E:\...\ETR.Video.Recorder.exe */
const RECORDER_EXE =
  process.env.REACT_APP_RECORDER_EXE_PATH?.trim() || 'ETR.Video.Recorder.exe';

export function buildDesktopRecorderUrl(sessionId: string): string {
  return `${DESKTOP_RECORDER_PROTOCOL}://start?sessionId=${encodeURIComponent(sessionId)}`;
}

export function buildDesktopRecorderHandshakeUrl(handshakeCode: string): string {
  const params = new URLSearchParams();
  params.set('handshake', handshakeCode);
  const apiBase = (process.env.REACT_APP_API_URL ?? '').trim();
  if (apiBase) {
    params.set('apiBaseUrl', apiBase.replace(/\/$/, ''));
  }
  return `${DESKTOP_RECORDER_PROTOCOL}://start?${params.toString()}`;
}

export function getDesktopRecorderLaunchCommand(sessionId: string): string {
  const id = sessionId.trim();
  const exe = RECORDER_EXE.includes(' ') ? `"${RECORDER_EXE}"` : RECORDER_EXE;
  return `${exe} --session-id=${id}`;
}

export async function createDesktopRecorderHandshake(
  sessionId: string,
  ticketId?: string | null
): Promise<string> {
  const body: { sessionId: string; ticketId?: string } = { sessionId: sessionId.trim() };
  if (ticketId?.trim()) body.ticketId = ticketId.trim();
  const response = await api.post<{ code: string }>('/recorder/handshake', body);
  return response.data.code;
}

/**
 * Creates a one-time handshake and opens the desktop app (no JWT in the URL).
 * Falls back to session-id launch command hint if the protocol is not registered.
 */
export async function openDesktopRecorder(
  sessionId: string,
  ticketId?: string | null
): Promise<void> {
  if (!sessionId?.trim()) {
    toast.error('Create a session first before opening the desktop recorder.');
    return;
  }

  try {
    const code = await createDesktopRecorderHandshake(sessionId, ticketId);
    const launchUrl = buildDesktopRecorderHandshakeUrl(code);
    // Avoid full-page navigation; some browsers handle custom protocols more reliably this way.
    const link = document.createElement('a');
    link.href = launchUrl;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Opening desktop recorder…', { id: 'desktop-recorder-launch', duration: 4000 });
    toast(
      'If the app did not open, install/register the recorder or use “Copy launch command”.',
      { id: 'desktop-recorder-launch-hint', duration: 8000, icon: 'ℹ️' }
    );
  } catch (err: unknown) {
    console.error('Desktop recorder handshake failed', err);
    const msg =
      err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
            ?.message
        : null;
    toast.error(
      msg
        ? `Could not start secure recorder login: ${msg}`
        : 'Could not start secure recorder login. Check API connection, then try again.',
      { duration: 10000 }
    );
    const link = document.createElement('a');
    link.href = buildDesktopRecorderUrl(sessionId.trim());
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast(
      'Opened recorder with session ID only — you must sign in manually in the desktop app.',
      { duration: 8000, icon: '⚠️' }
    );
  }
}

export function copyDesktopRecorderCommand(sessionId: string): void {
  const cmd = getDesktopRecorderLaunchCommand(sessionId);
  void navigator.clipboard?.writeText(cmd);
  toast.success('Launch command copied — paste into PowerShell or Run dialog', { duration: 5000 });
}

export function copySessionId(sessionId: string): void {
  void navigator.clipboard?.writeText(sessionId.trim());
  toast.success('Session ID copied');
}
